import { HeatmapLayer } from "@deck.gl/aggregation-layers";
import { useCallback, useMemo } from "react";
import type {
    AdminEmissionsRecordsStatsListProviderWithSourceParameterInner,
    AnalyticsMapSiteAnalyticsListRequest,
    SiteMapAnalytics,
} from "../../../apiClient/generated";
import { useAnalyticsApiClient } from "../../../hooks";
import { HEATMAP_COLOR_RANGES, type HeatmapColorRampId } from "../constants";
import type { FilterByAreaDef } from "../hooks";
import { useStable } from "./areaFilter";
import { useMapDataLoader } from "./dataLoader";
import { useMapData } from "./mapDataAndFilters";
import { useMap } from "./mapState";
import { cleanFilters } from "./utils";

type AnalyticsHeatmapFilters = Omit<
    AnalyticsMapSiteAnalyticsListRequest,
    "providerWithSource" | "cursor" | "locationWithin" | "locationPreset"
> & {
    providerWithSource:
        | AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[]
        | "all";
};

interface HeatmapConfig {
    sourceData: "percentage_of_obs" | "count_emissions" | "count_non_detects";
    colorMap: HeatmapColorRampId;
    opacity: number;
}

/**
 * Custom hook that loads analytics data on map.
 *
 * Used to build interactive heatmaps.
 * WARNING: This is resource intensive and downloads
 *          a large amount of data!
 */
export const useAnalyticsOnMap = (
    mapId: string,
    enabled: boolean,
    filters: AnalyticsHeatmapFilters,
    heatmapConfig: HeatmapConfig,
    filterByArea?: FilterByAreaDef,
) => {
    const { sourceData, colorMap, opacity } = heatmapConfig;
    const apiClient = useAnalyticsApiClient();
    const stableMapId = useStable(mapId);
    const {
        debounced: { viewState, areaOnScreen },
    } = useMap(stableMapId);
    const currentZoom = useMemo(() => viewState?.zoom, [viewState]);

    const { selectedContext } = useMapData(stableMapId);
    const anyContextSelected = useMemo(() => {
        return Object.values(selectedContext).some(
            (value) => value !== undefined,
        );
    }, [selectedContext]);

    const analyticsDataLoader = useMapDataLoader<SiteMapAnalytics>({
        fetchFn: useCallback(
            async (areaToFetch, abortSignal, cursor) => {
                const { cleanFilterValues, shortCircuit } =
                    cleanFilters<AnalyticsHeatmapFilters>(filters);
                if (shortCircuit) {
                    return {
                        results: [] as SiteMapAnalytics[],
                    };
                }
                const response = await apiClient.analyticsMapSiteAnalyticsList(
                    {
                        ...cleanFilterValues,
                        providerWithSource: cleanFilterValues.providerWithSource
                            ? (JSON.stringify(
                                  cleanFilterValues.providerWithSource,
                              ) as any)
                            : undefined,
                        locationWithin: areaToFetch
                            ? JSON.stringify(areaToFetch)
                            : undefined,
                        locationPreset: filterByArea?.selectedFilters
                            ? filterByArea.selectedFilters
                            : undefined,
                        cursor,
                    },
                    {
                        signal: abortSignal,
                    },
                );
                return response;
            },
            [filters, filterByArea, apiClient],
        ),
        zoomToStartFetching: 1,
        enabled:
            enabled &&
            !anyContextSelected &&
            (filters.providerWithSource.length > 0 ||
                filters.providerWithSource === "all"),
        areaOnScreen,
        zoom: currentZoom,
        areaFilter:
            filterByArea && !filterByArea.selectedFilters
                ? filterByArea.drawnShape
                : undefined,
    });

    const heatmapPoints = useMemo(() => {
        const data = analyticsDataLoader.data;
        if (!data || data.length === 0) return [];
        const points: { position: [number, number]; weight: number }[] = [];
        for (const item of data) {
            if (item.location === null) continue;
            let weight: number;
            if (sourceData === "count_emissions") {
                weight = item.emissionCount;
            } else if (sourceData === "count_non_detects") {
                weight = item.nondetectsCount;
            } else {
                weight = item.emissionRatio * 100;
            }
            if (weight <= 0) continue;
            points.push({
                position: item.location as [number, number],
                weight,
            });
        }
        return points;
    }, [analyticsDataLoader.data, sourceData]);

    const analyticsLayer = useMemo(() => {
        if (!enabled || anyContextSelected || currentZoom > 12) {
            return [];
        }
        if (heatmapPoints.length === 0) {
            return [];
        }
        return [
            new HeatmapLayer<{ position: [number, number]; weight: number }>({
                id: "emission-heatmap",
                data: heatmapPoints,
                getPosition: (d) => d.position,
                getWeight: (d) => d.weight,
                colorRange: HEATMAP_COLOR_RANGES[colorMap],
                radiusPixels: 60,
                opacity: opacity / 100,
                visible: true,
                pickable: false,
                weightsTextureSize: 512,
            }),
        ];
    }, [
        enabled,
        anyContextSelected,
        currentZoom,
        heatmapPoints,
        colorMap,
        opacity,
    ]);

    return {
        layer: analyticsLayer,
        loading: analyticsDataLoader.loading,
        data: analyticsDataLoader.data,
    };
};
